/**
 * Copyright (c) 2010-2025 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { DeviceOrientation, DevicePermissionState, getDeviceOrientation, requestPermission } from 'fulltilt';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { from, of, Subject } from 'rxjs';
import { catchError, filter, mergeMap, take, tap, throttleTime } from 'rxjs/operators';
import { LoggerService } from '../../shared/services/logger.service';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { EnableCompassReading, MapCreated, SetCompassDirection } from '../state/active-map.actions';
import { PermissionRequestComponent } from '../components/permission-request.component';

@Injectable()
export class OrientationService {
    constructor(
        private logger: LoggerService,
        private store: Store,
        private actions$: Actions,
        private dialog: MatDialog
    ) {
        this.actions$.pipe(ofActionSuccessful(MapCreated)).subscribe(() => {
            this.store.dispatch(new EnableCompassReading());
        });

        this.actions$
            .pipe(
                ofActionSuccessful(EnableCompassReading),
                tap(() => {
                    this.watchOrientation$().subscribe((heading) =>
                        this.store.dispatch(new SetCompassDirection(heading))
                    );
                })
            )
            .subscribe();
    }

    watchOrientation$() {
        const permissionCheck = from(requestPermission('orientation')).pipe(take(1));
        return permissionCheck.pipe(
            mergeMap((permission: DevicePermissionState) => {
                switch (permission.orientation) {
                    case 'granted':
                        return of(true);
                    case 'denied':
                        return of(false);
                    case 'prompt':
                        return this.dialog.open(PermissionRequestComponent, {}).afterClosed();
                }
            }),
            filter(Boolean),
            mergeMap(() => from(getDeviceOrientation({ type: 'world', requireLiveData: true })).pipe(take(1))),
            mergeMap((control: DeviceOrientation) => {
                const compassSubject = new Subject<number>();
                control.listen(() => {
                    const orientation = control.getScreenAdjustedEuler();
                    compassSubject.next(360 - orientation.alpha);
                });
                return compassSubject.asObservable();
            }),
            throttleTime(30),
            catchError((err: Error) => {
                this.logger.warn(err.message);
                return of(null);
            })
        );
    }
}
